// libraries
import { useTranslation } from 'react-i18next';
import { type MouseEventHandler, useState } from 'react';

// components
import { ContextMenu } from 'components/mrp/components/ContextMenu';
import { Tooltip, useTheme } from '@mui/material';
import { EntityInfoTable } from 'components/shared/EntityInfoTable';

// styles
import {
  ActionsContainer,
  CalendarHeaderButtonIcon,
  Container,
  ContentContainer,
  HeaderContentContainer,
  LabelContainer,
  ProgressBarContainer,
  SlimLinearProgress,
  EntitySubLabel
} from './CalendarHeader.styles';

// types
import type { CalendarHeaderProps } from './CalendarHeader.types';
import type { ContextMenuAction } from 'components/mrp/components/ContextMenu/ContextMenu.types';
import type { EntityInfoRow } from 'components/shared/EntityInfoTable';

// stores
import { useCalendarStore } from 'components/mrp/stores/calendarStore/calendarStore';
import { useShallow } from 'zustand/shallow';

// utils
import { getEntityIcon, getUniqueEntities } from 'components/mrp/mrp.utils';
import { formatDate } from 'utils/date';

// constants
import { EntityType } from 'components/mrp/mrp.consts';
import { useEntityListener } from 'components/mrp/mrp.hooks';
import { EntityPickerLabel } from '../EntityPickerLabel';
import {
  formatTreatments,
  getCloseActions,
  getEmployeeFunctionDisplayText,
  getEntityInfo,
  getFormattedTreatments,
  getLabel,
  getMergeActions,
  getMoveActions,
  getNavigateToAppointmentsOverviewAction,
  getSplitActions
} from './CalendarHeader.utils';

const CalendarHeader = ({ displayData }: CalendarHeaderProps) => {
  'use memo';

  const { t } = useTranslation('common', { keyPrefix: 'mrp' });
  const theme = useTheme();

  const [isEntityPickerOpen, setIsEntityPickerOpen] = useState<boolean>(false);

  const {
    calendars,
    closeCalendar,
    toggleCalendarPin,
    mergeCalendars,
    moveCalendar,
    splitCalendar
  } = useCalendarStore(
    useShallow((state) => ({
      calendars: state.calendars,
      closeCalendar: state.control.closeCalendar,
      toggleCalendarPin: state.control.toggleCalendarPin,
      mergeCalendars: state.control.mergeCalendars,
      moveCalendar: state.control.moveCalendar,
      splitCalendar: state.control.splitCalendar
    }))
  );

  const planningEntityIdentifiers = displayData.sources.flatMap(
    ({ planningEntityIdentifier }) => planningEntityIdentifier
  );

  const { isLoading, planningEntities } = useEntityListener({
    calendarId: displayData.calendarId,
    planningEntityIdentifiers,
    listen: false
  });

  const uniqueEntities = getUniqueEntities(displayData.sources);

  const planningEntityForHeader =
    uniqueEntities.length === 1 ? planningEntities[0] ?? null : null;

  const label = getLabel(uniqueEntities, t);

  const iconEntityType =
    uniqueEntities.length > 1
      ? 'MIX'
      : uniqueEntities[0]?.planningEntityIdentifier.planningEntityType;

  const icon = getEntityIcon(iconEntityType, undefined, 'LARGE', true);

  const entityInfo = getEntityInfo(uniqueEntities, planningEntityForHeader);

  const splitActions =
    uniqueEntities.length > 1
      ? [
          getSplitActions({
            splitCalendar,
            displayData
          })
        ]
      : [];

  const moveActions =
    uniqueEntities.length === 1
      ? [
          {
            value: t('move'),
            subActions: getMoveActions({
              calendars,
              displayData,
              moveCalendar
            })
          } as ContextMenuAction
        ]
      : [];

  const action = getNavigateToAppointmentsOverviewAction({ displayData });
  const navigateToAppointmentsOverviewActions = action ? [action] : [];

  const actions: ContextMenuAction[] = [
    ...moveActions,
    getCloseActions({
      calendars,
      displayData,
      closeCalendar
    }),
    getMergeActions({
      mergeCalendars,
      displayData,
      calendars
    }),
    ...splitActions,
    ...navigateToAppointmentsOverviewActions
  ];

  const middleClickClose: MouseEventHandler = (ev) => {
    if (ev.button != 1) return;

    closeCalendar(displayData.calendarId, true);
    ev.preventDefault();
    ev.stopPropagation();
  };

  const handleEntityPickerOpen = () => {
    setIsEntityPickerOpen(true);
  };

  const handleTogglePin = () => {
    toggleCalendarPin(displayData.calendarId);
  };

  const handleClose = () => {
    closeCalendar(displayData.calendarId, true);
  };

  const formattedTreatments = getFormattedTreatments(
    entityInfo?.treatments || []
  );

  const treatmentDisplayText =
    formattedTreatments.length === 0
      ? t('noTreatments')
      : formattedTreatments.map((row) => row.groupName).join(', ');

  const employeeFunctionDisplayText = getEmployeeFunctionDisplayText(
    uniqueEntities,
    planningEntityForHeader,
    t
  );

  const entityInfoRows: EntityInfoRow[] = !entityInfo
    ? []
    : [
        {
          label: t('entityInfo.name'),
          value: entityInfo.displayName
        },
        {
          label: t('entityInfo.dateOfBirth'),
          value: formatDate(entityInfo.dateOfBirth),
          hidden: !entityInfo.dateOfBirth
        },
        {
          label: t('entityInfo.patientNumber'),
          value: entityInfo.number,
          hidden: !entityInfo.number
        },
        {
          label: t('entityInfo.treatments'),
          value: formatTreatments(formattedTreatments, theme.palette.divider),
          hidden: formattedTreatments.length === 0
        }
      ];

  if (uniqueEntities.length === 0) return null;

  return (
    <Container onAuxClick={middleClickClose} onMouseDown={middleClickClose}>
      <HeaderContentContainer>
        <Tooltip
          arrow
          enterDelay={500}
          enterNextDelay={500}
          title={
            entityInfoRows.length > 0 ? (
              <EntityInfoTable rows={entityInfoRows} />
            ) : (
              ''
            )
          }
        >
          <span>{icon}</span>
        </Tooltip>

        <ContentContainer>
          <LabelContainer>
            <EntityPickerLabel
              label={label}
              calendarId={displayData.calendarId}
              isPopoverOpen={isEntityPickerOpen}
              setIsPopoverOpen={setIsEntityPickerOpen}
            />
          </LabelContainer>
          {iconEntityType === EntityType.CLIENT && (
            <EntitySubLabel>{treatmentDisplayText}</EntitySubLabel>
          )}
          {iconEntityType === EntityType.EMPLOYEE &&
            employeeFunctionDisplayText != null && (
              <EntitySubLabel>{employeeFunctionDisplayText}</EntitySubLabel>
            )}
        </ContentContainer>

        <ActionsContainer>
          <ContextMenu id="panel-header-context-menu" actions={actions}>
            <CalendarHeaderButtonIcon type="ellipsis-stroke-vertical" />
          </ContextMenu>

          <CalendarHeaderButtonIcon
            type="arrow-right-arrow-left"
            onClick={handleEntityPickerOpen}
          />

          <CalendarHeaderButtonIcon
            type={displayData.isPinned ? 'thumbtack-fill' : 'thumbtack'}
            onClick={handleTogglePin}
          />

          <CalendarHeaderButtonIcon type="xmark" onClick={handleClose} />
        </ActionsContainer>
      </HeaderContentContainer>
      <ProgressBarContainer isVisible={isLoading}>
        <SlimLinearProgress variant="indeterminate" />
      </ProgressBarContainer>
    </Container>
  );
};

export default CalendarHeader;
